/**
 * Main App component - Pipeline Builder
 */

import { useState, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { NodePalette } from './components/NodePalette';
import { PipelineCanvas } from './components/PipelineCanvas';
import { ExecutionMonitor } from './components/ExecutionMonitor';
import { ExecutionList } from './components/ExecutionList';
import { PipelineList } from './components/PipelineList';
import { NodeConfigSidebar } from './components/NodeConfigSidebar';
import { DataSourceConfig } from './components/DataSourceConfig';
import { ExecutionWizard } from './components/ExecutionWizard';
import { ConditionalNodeConfig } from './components/ConditionalNodeConfig';
import { ItemSearchConfig } from './components/ItemSearchConfig';
import type { ItemSearchNodeConfig } from './components/ItemSearchConfig';
import { CreateReferenceConfig } from './components/CreateReferenceConfig';
import type { CreateReferenceNodeConfig } from './components/CreateReferenceConfig';
import { PushbackConfig } from './components/PushbackConfig';
import type { PushbackNodeConfig } from './components/PushbackConfig';
import { CreateItemConfig } from './components/CreateItemConfig';
import type { CreateItemNodeConfig } from './components/CreateItemConfig';
import { EdgeConfigSidebar } from './components/EdgeConfigSidebar';
import { AgentLibrary } from './components/AgentLibrary';
import { SettingsPage } from './components/SettingsPage';
import { AgentPanel } from './components/AgentPanel';
import { LoadingScreen } from './components/LoadingScreen';
import { LoginGate } from './components/LoginGate';
import { usePipeline } from './hooks/usePipeline';
import { useOpenAPISchema } from './hooks/useOpenAPISchema';
import pipelineApi, { ITEMLESS_DATA_SOURCE_TASK_TYPES, isDataSourceNode, sanitizeStepName } from './api/client';
import { getUpstreamNodes } from './components/ValueBindingEditor';
import type { PipelineDefinition, PipelineNode, PipelineEdge, StepCondition } from './types/pipeline';
import { validatePipeline, formatValidationErrors } from './utils/validation';
import { copyToClipboard } from './utils/clipboard';
import { getApiKeyCookie, clearApiKeyCookie } from './utils/authCookie';
import { ActivityIcon, ArrowLeftIcon, CheckIcon, CopyIcon, PlayIcon } from './components/icons';
import './App.css';
import './styles/design-system.css';

type View = 'list' | 'builder' | 'executions' | 'execution-monitor' | 'agents' | 'settings';

// Load persisted state from localStorage
const loadPersistedState = () => {
  try {
    const savedState = localStorage.getItem('raiqon_pipeline_ui_state');
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (e) {
    console.error('Failed to load persisted state:', e);
  }
  return null;
};

// Save state to localStorage
const savePersistedState = (view: View, pipelineId?: string) => {
  try {
    localStorage.setItem('raiqon_pipeline_ui_state', JSON.stringify({
      view,
      pipelineId,
      timestamp: Date.now(),
    }));
  } catch (e) {
    console.error('Failed to save state:', e);
  }
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => getApiKeyCookie() !== null);

  useEffect(() => {
    const handleAuthInvalid = () => setIsAuthenticated(false);
    window.addEventListener('raiqon:auth-invalid', handleAuthInvalid);
    return () => window.removeEventListener('raiqon:auth-invalid', handleAuthInvalid);
  }, []);

  const persistedState = loadPersistedState();
  // Only restore non-builder views immediately; builder view is restored after pipeline loads
  const initialView = persistedState?.view === 'builder' ? 'list' : (persistedState?.view || 'list');
  const [currentView, setCurrentView] = useState<View>(initialView);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  // Bumped on every explicit (re-)open of the monitor so React remounts <ExecutionMonitor>
  // even when executionId is unchanged (same item re-triggered) - forces a clean poll cycle
  // instead of showing stale state from the previous run.
  const [executionTriggerNonce, setExecutionTriggerNonce] = useState(0);
  const [isOpeningWizard, setIsOpeningWizard] = useState(false);
  const [showExecutionWizard, setShowExecutionWizard] = useState(false);
  const [selectedNode, setSelectedNode] = useState<PipelineNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<PipelineEdge | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isLoadingPipeline, setIsLoadingPipeline] = useState(false);
  const [executionsFilterPipelineId, setExecutionsFilterPipelineId] = useState<string | undefined>();
  const [agentPanelState, setAgentPanelState] = useState<{
    mode: 'library' | 'edit' | 'sandbox';
    agentId?: string;
    onPick?: (agentId: string) => void;
    onAgentUpdated?: (agentId: string) => void;
    onClose?: () => void;
  } | null>(null);
  // Bumped whenever an agent is updated via the Agent Panel so PipelineCanvas's
  // node badges (agent name/model) pick up the change immediately instead of
  // only after the panel closes.
  const [agentsRefreshNonce, setAgentsRefreshNonce] = useState(0);

  const {
    pipeline,
    setPipeline,
    setExistingPipeline,
    duplicatePipeline,
    loading,
    error,
    createNewPipeline,
    savePipeline,
  } = usePipeline();

  const { getTaskSchema } = useOpenAPISchema();

  // Restore pipeline on mount if state was persisted
  useEffect(() => {
    const persistedState = loadPersistedState();
    if (persistedState?.pipelineId && persistedState.view === 'builder') {
      // Restore the pipeline, then switch to builder view
      setIsLoadingPipeline(true);
      pipelineApi.getPipeline(persistedState.pipelineId)
        .then(fullPipeline => {
          setExistingPipeline(fullPipeline);
          // Only switch to builder view after pipeline is loaded
          setCurrentView('builder');
        })
        .catch(err => {
          console.error('Failed to restore pipeline:', err);
          setCurrentView('list');
        })
        .finally(() => setIsLoadingPipeline(false));
    }
  }, [setExistingPipeline]);

  // Persist state when view or pipeline changes
  useEffect(() => {
    savePersistedState(currentView, pipeline?.id);
  }, [currentView, pipeline?.id]);

  // Right-side panels belong to the open workflow; close them on page switch or workflow exit
  useEffect(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setAgentPanelState(null);
  }, [currentView, pipeline?.id]);

  const handleNewPipeline = () => {
    createNewPipeline();
    setCurrentView('builder');
  };

  const handleOpenPipeline = async (pipeline: PipelineDefinition) => {
    // Load full workflow details including nodes and edges
    setIsLoadingPipeline(true);
    try {
      const fullPipeline = await pipelineApi.getPipeline(pipeline.id);
      setExistingPipeline(fullPipeline);
      setCurrentView('builder');
    } catch (err) {
      console.error('Failed to load pipeline details:', err);
      alert('Failed to load pipeline: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsLoadingPipeline(false);
    }
  };

  const handleDuplicatePipeline = async (pipeline: PipelineDefinition) => {
    setIsLoadingPipeline(true);
    try {
      duplicatePipeline(await pipelineApi.getPipeline(pipeline.id));
      setCurrentView('builder');
    } catch (err) {
      console.error('Failed to load pipeline for duplication:', err);
      alert('Failed to duplicate pipeline: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsLoadingPipeline(false);
    }
  };

  const handleOpenExecution = (executionId: string) => {
    setCurrentExecutionId(executionId);
    setExecutionTriggerNonce(n => n + 1);
    setCurrentView('execution-monitor');
  };

  const handleNodeClick = (node: PipelineNode) => {
    setSelectedNode(node);
    setSelectedEdge(null); // Close edge sidebar when opening node sidebar
  };

  const handleEdgeClick = (edge: PipelineEdge) => {
    setSelectedEdge(edge);
    setSelectedNode(null); // Close node sidebar when opening edge sidebar
  };

  const handleNodeConfigSave = (
    config: Record<string, any>,
    agentId?: string,
    label?: string,
    agentVersion?: number
  ) => {
    if (!pipeline || !selectedNode) return;

    // Update the node's config, agent_id/agent_version, and label
    const updatedNodes = pipeline.nodes.map(n =>
      n.id === selectedNode.id
        ? { ...n, config, agent_id: agentId, agent_version: agentVersion, label: label?.trim() ? label.trim() : n.label }
        : n
    );

    setPipeline({
      ...pipeline,
      nodes: updatedNodes,
      edges: pipeline.edges,
    });

    setSelectedNode(null);
  };

  const handleConditionalNodeConfigSave = (condition: StepCondition) => {
    if (!pipeline || !selectedNode) return;

    // Update the branch node's single condition
    const updatedNodes = pipeline.nodes.map(n =>
      n.id === selectedNode.id
        ? { ...n, config: { ...n.config, condition } }
        : n
    );

    setPipeline({
      ...pipeline,
      nodes: updatedNodes,
    });

    setSelectedNode(null);
  };

  const handleDataSourceConfigSave = (config: { name: string; outline_depth?: number | null; value_source_field?: string | null }) => {
    if (!pipeline || !selectedNode) return;

    const updatedNodes = pipeline.nodes.map(n =>
      n.id === selectedNode.id
        ? { ...n, config: { ...n.config, ...config } }
        : n
    );

    setPipeline({
      ...pipeline,
      nodes: updatedNodes,
    });

    setSelectedNode(null);
  };

  const handleItemSearchConfigSave = (config: ItemSearchNodeConfig) => {
    if (!pipeline || !selectedNode) return;

    // Item search nodes have no parameter bindings - their entire config is
    // search_kind/max_results/score_threshold, so replace it wholesale.
    const updatedNodes = pipeline.nodes.map(n =>
      n.id === selectedNode.id
        ? { ...n, config: { ...config } }
        : n
    );

    setPipeline({
      ...pipeline,
      nodes: updatedNodes,
    });

    setSelectedNode(null);
  };

  const handleCreateReferenceConfigSave = (config: CreateReferenceNodeConfig) => {
    if (!pipeline || !selectedNode) return;

    // Create reference nodes have no parameter bindings - their entire config
    // is reference_type/field_id/direction/cardinality, so replace it wholesale.
    const updatedNodes = pipeline.nodes.map(n =>
      n.id === selectedNode.id
        ? { ...n, config: { ...config } }
        : n
    );

    setPipeline({
      ...pipeline,
      nodes: updatedNodes,
    });

    setSelectedNode(null);
  };

  const handlePushbackConfigSave = (config: PushbackNodeConfig) => {
    if (!pipeline || !selectedNode) return;

    // Pushback nodes have no parameter bindings - their entire config is
    // target_field/operation/value_binding, so replace it wholesale.
    const updatedNodes = pipeline.nodes.map(n =>
      n.id === selectedNode.id
        ? { ...n, config: { ...config } }
        : n
    );

    setPipeline({
      ...pipeline,
      nodes: updatedNodes,
    });

    setSelectedNode(null);
  };

  const handleCreateItemConfigSave = (config: CreateItemNodeConfig) => {
    if (!pipeline || !selectedNode) return;

    // Like pushback, a create_item node's entire config is its target/field
    // mappings/link settings, so replace it wholesale.
    const updatedNodes = pipeline.nodes.map(n =>
      n.id === selectedNode.id
        ? { ...n, config: { ...config } }
        : n
    );

    setPipeline({
      ...pipeline,
      nodes: updatedNodes,
      edges: pipeline.edges,
    });

    setSelectedNode(null);
  };


  const handleEdgeConfigSave = (edgeId: string, config: Partial<PipelineEdge>) => {
    if (!pipeline) return;

    // Update the edge's config
    const updatedEdges = pipeline.edges.map(e =>
      e.id === edgeId ? { ...e, ...config } : e
    );

    setPipeline({
      ...pipeline,
      edges: updatedEdges,
    });

    setSelectedEdge(null);
  };

  const handleStartEditName = () => {
    if (pipeline) {
      setEditedName(pipeline.metadata.name);
      setIsEditingName(true);
    }
  };

  const handleSaveName = () => {
    if (pipeline && editedName.trim()) {
      setPipeline({
        ...pipeline,
        metadata: {
          ...pipeline.metadata,
          name: editedName.trim(),
        },
      });
    }
    setIsEditingName(false);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEditName();
    }
  };

  const handleSave = async () => {
    if (!pipeline) return;

    // Validate before saving
    const errors = validatePipeline(pipeline);
    if (errors.length > 0) {
      alert(formatValidationErrors(errors));
      return;
    }

    try {
      await savePipeline();
      setSaveSuccess(true);
    } catch (err) {
      console.error('Failed to save pipeline:', err);
      alert((err as any)?.response?.data?.detail ?? 'Failed to save pipeline');
    }
  };

  // Clear save success indicator after 3 seconds
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  const handleOpenExecutionWizard = async () => {
    if (!pipeline) return;

    // Validate before execution
    const errors = validatePipeline(pipeline);
    if (errors.length > 0) {
      alert(formatValidationErrors(errors));
      return;
    }

    setIsOpeningWizard(true);
    try {
      // Save the pipeline so the wizard triggers the latest saved version
      await savePipeline();
      setShowExecutionWizard(true);
    } catch (err) {
      console.error('Failed to save pipeline:', err);
      alert((err as any)?.response?.data?.detail ?? 'Failed to save pipeline');
    } finally {
      setIsOpeningWizard(false);
    }
  };

  if (!isAuthenticated) {
    return <LoginGate onSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="app">
      {isLoadingPipeline && <LoadingScreen variant="overlay" label="Loading pipeline..." />}
      <Sidebar
        currentView={currentView}
        onViewChange={(view) => {
          // The sidebar entry lists runs across all workflows, not the last opened one's.
          if (view === 'executions') setExecutionsFilterPipelineId(undefined);
          setCurrentView(view);
        }}
        onLogout={() => {
          clearApiKeyCookie();
          setIsAuthenticated(false);
        }}
      />

      <div className="app-content">
        {currentView === 'list' ? (
          <PipelineList
            onOpenPipeline={handleOpenPipeline}
            onDuplicatePipeline={handleDuplicatePipeline}
            onCreateNew={handleNewPipeline}
            onOpenExecution={handleOpenExecution}
          />
        ) : currentView === 'agents' ? (
          <AgentLibrary />
        ) : currentView === 'settings' ? (
          <SettingsPage />
        ) : currentView === 'executions' ? (
          <ExecutionList
            onOpenExecution={handleOpenExecution}
            pipelineId={executionsFilterPipelineId}
            onBack={executionsFilterPipelineId ? () => setCurrentView('builder') : undefined}
          />
        ) : (
          <>
            <div className="app-toolbar">
              <div className="toolbar-left">
                <button
                  className="button-secondary"
                  onClick={() => setCurrentView(currentView === 'execution-monitor' ? 'executions' : 'list')}
                  title={currentView === 'execution-monitor' ? 'Back to executions' : 'Back to workflow list'}
                >
                  <ArrowLeftIcon /> Back
                </button>
                {currentView === 'builder' && pipeline ? (
                  <div className="pipeline-name-editor">
                    {isEditingName ? (
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onBlur={handleSaveName}
                        onKeyDown={handleNameKeyDown}
                        className="pipeline-name-input-large"
                        autoFocus
                      />
                    ) : (
                      <div>
                        <h1
                          className="pipeline-name-title"
                          onClick={handleStartEditName}
                          title="Click to edit pipeline name"
                        >
                          {pipeline.metadata.name}
                        </h1>
                        <div className="pipeline-id-display" title="Click to copy">
                          {pipeline.id}
                          <button
                            className="copy-id-btn-inline"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(pipeline.id);
                              alert('Workflow ID copied to clipboard!');
                            }}
                            title="Copy to clipboard"
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <h2 className="toolbar-title">Execution Monitor</h2>
                )}
              </div>

              <div className="toolbar-actions">
                {currentView === 'builder' ? (
                  <>
                    <button
                      className="button-secondary"
                      onClick={() => {
                        setExecutionsFilterPipelineId(pipeline?.id);
                        setCurrentView('executions');
                      }}
                      disabled={!pipeline}
                      title="View execution history for this workflow"
                    >
                      <ActivityIcon /> Executions
                    </button>
                    <button
                      className={`button-secondary ${saveSuccess ? 'save-success' : ''}`}
                      onClick={handleSave}
                      disabled={loading || !pipeline}
                    >
                      {loading ? <LoadingScreen variant="compact" label="Saving..." /> : saveSuccess ? <><CheckIcon /> Saved</> : 'Save'}
                    </button>
                    <button
                      className="button-primary"
                      onClick={handleOpenExecutionWizard}
                      disabled={isOpeningWizard || !pipeline || pipeline.nodes.length === 0}
                    >
                      {isOpeningWizard ? <LoadingScreen variant="compact" label="Saving..." /> : <><PlayIcon /> Execute Workflow</>}
                    </button>
                  </>
                ) : (
                  <button className="button-secondary" onClick={() => setCurrentView('executions')}>
                    Back to Executions
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="app-error">
                <strong>Error:</strong> {error.message}
              </div>
            )}

            <div className="app-main">
              {currentView === 'builder' ? (
                <>
                  {pipeline && <NodePalette />}
                  <PipelineCanvas
                    pipeline={pipeline}
                    onPipelineChange={setPipeline}
                    onNodeClick={handleNodeClick}
                    onEdgeClick={handleEdgeClick}
                    agentsRefreshKey={agentsRefreshNonce}
                  />
                </>
              ) : (
                <ExecutionMonitor
                  key={`${currentExecutionId}-${executionTriggerNonce}`}
                  executionId={currentExecutionId}
                  onClose={() => setCurrentView('executions')}
                  onOpenPipeline={async (pipelineId) => {
                    // Load the pipeline and navigate to builder
                    setIsLoadingPipeline(true);
                    try {
                      const pipelineData = await pipelineApi.getPipeline(pipelineId);
                      setExistingPipeline(pipelineData);
                      setCurrentView('builder');
                    } catch (err) {
                      console.error('Failed to load pipeline:', err);
                      alert('Failed to load pipeline');
                    } finally {
                      setIsLoadingPipeline(false);
                    }
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>

      {selectedNode && selectedNode.task_type === 'branch' ? (
        <ConditionalNodeConfig
          node={selectedNode}
          pipeline={pipeline}
          onSave={handleConditionalNodeConfigSave}
          onClose={() => setSelectedNode(null)}
        />
      ) : selectedNode && isDataSourceNode(selectedNode) ? (
        <DataSourceConfig
          key={selectedNode.id}
          node={selectedNode}
          pipeline={pipeline}
          onSave={handleDataSourceConfigSave}
          onClose={() => setSelectedNode(null)}
        />
      ) : selectedNode && (
        selectedNode.task_type === 'item_search' ||
        selectedNode.task_type === 'reference_graph_query'
      ) ? (
        <ItemSearchConfig
          node={selectedNode}
          onSave={handleItemSearchConfigSave}
          onClose={() => setSelectedNode(null)}
        />
      ) : selectedNode && selectedNode.task_type === 'create_reference' ? (
        <CreateReferenceConfig
          node={selectedNode}
          onSave={handleCreateReferenceConfigSave}
          onClose={() => setSelectedNode(null)}
        />
      ) : selectedNode && selectedNode.task_type === 'pushback' ? (
        <PushbackConfig
          node={selectedNode}
          pipeline={pipeline}
          availableWorkflowInputs={(pipeline?.metadata.inputs || []).map((i: any) => i.name)}
          onSave={handlePushbackConfigSave}
          onClose={() => setSelectedNode(null)}
        />
      ) : selectedNode && selectedNode.task_type === 'create_item' ? (
        <CreateItemConfig
          node={selectedNode}
          pipeline={pipeline}
          availableWorkflowInputs={(pipeline?.metadata.inputs || []).map((i: any) => i.name)}
          onSave={handleCreateItemConfigSave}
          onClose={() => setSelectedNode(null)}
        />
      ) : selectedNode ? (
        <NodeConfigSidebar
          node={selectedNode}
          onSave={handleNodeConfigSave}
          onClose={() => setSelectedNode(null)}
          taskSchema={selectedNode ? getTaskSchema(selectedNode.task_type) : undefined}
          onOpenAgentPanel={setAgentPanelState}
        />
      ) : null}

      <EdgeConfigSidebar
        edge={selectedEdge}
        pipeline={pipeline}
        onSave={handleEdgeConfigSave}
        onClose={() => setSelectedEdge(null)}
      />

      {showExecutionWizard && pipeline && (
        <ExecutionWizard
          pipelineId={pipeline.id}
          pipelineName={pipeline.metadata.name}
          workflowInputs={pipeline.metadata.inputs || []}
          onClose={() => setShowExecutionWizard(false)}
          onOpenExecution={(executionId) => {
            setShowExecutionWizard(false);
            handleOpenExecution(executionId);
          }}
        />
      )}

      {agentPanelState && (
        <AgentPanel
          initialMode={agentPanelState.mode}
          initialAgentId={agentPanelState.agentId}
          // The panel is opened from the selected agent node - let the agent's prompt
          // variables pick fields out of that node's upstream steps' outputs.
          upstreamSteps={
            selectedNode
              ? getUpstreamNodes(pipeline, selectedNode.id).map(n => ({
                  stepName: sanitizeStepName(n.id),
                  label: n.label || n.id,
                  agentId: n.agent_id,
                }))
              : undefined
          }
          connectedInputs={
            selectedNode && pipeline
              ? pipeline.edges
                  .filter(e => e.target === selectedNode.id)
                  .map(e => pipeline.nodes.find(n => n.id === e.source))
                  .filter((n): n is PipelineNode => !!n && isDataSourceNode(n))
                  .map(n => ({
                    name: n.config?.name || sanitizeStepName(n.id),
                    label: n.label || n.id,
                    itemless: ITEMLESS_DATA_SOURCE_TASK_TYPES.has(n.task_type),
                    taskType: n.task_type,
                  }))
              : undefined
          }
          selectedAgentId={agentPanelState.agentId}
          onSelectAgent={(agent) => {
            agentPanelState.onPick?.(agent.id);
            agentPanelState.onClose?.();
            setAgentPanelState(null);
          }}
          onAgentUpdated={(agent) => {
            agentPanelState.onAgentUpdated?.(agent.id);
            setAgentsRefreshNonce(n => n + 1);
          }}
          onClose={() => {
            agentPanelState.onClose?.();
            setAgentPanelState(null);
          }}
        />
      )}
    </div>
  );
}

export default App;
